import { ChangeDetectorRef, Component, EventEmitter, Input, Output, OnInit } from '@angular/core';
import { CalendarEvent } from 'src/app/models/calendarEvent';
import { CalendarService } from 'src/app/services/calendar.service';
import { DateHelperService } from 'src/app/services/dateHelper.service';

@Component({
  selector: 'app-calendar',
  templateUrl: './calendar.component.html',
  styleUrls: ['./calendar.component.scss'],
  standalone: false
})
export class CalendarComponent implements OnInit {
  @Input() date: Date = new Date();
  @Output() calendarEventSelected = new EventEmitter<CalendarEvent>();
  @Output() multipleEventsSelected = new EventEmitter<CalendarEvent[]>();

  public calendar: CalendarEvent[] | undefined;
  public calendarLoading: boolean = false;
  public calendarLoaded: boolean = false;
  public hasGoogleAccess = true;
  public hasMicrosoftAccess = true;
  public showSettings = false;
  public selectedCalenderId: string | undefined;
  public groupedCalendarEvents: { [key: string]: CalendarEvent[] } = {};
  // Start expanded on desktop to prevent CLS
  public isCalendarExpanded: boolean = window.innerWidth >= 1024;
  public isMobileExpanded: boolean = window.innerWidth >= 1024;
  public isConnectingMicrosoft: boolean = false;
  public isConnectingGoogle: boolean = false;

  constructor(
    private calendarService: CalendarService,
    private dateHelper: DateHelperService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit() {
    this.checkCalendarAccess();
    // On desktop, start expanded to prevent CLS
    // On mobile (<1024px), calendar is controlled by mobile toggle
    this.isCalendarExpanded = window.innerWidth >= 1024;
  }

  private checkCalendarAccess() {
    this.calendarService.hasGoogleAccess().subscribe((hasAccess) => {
      this.hasGoogleAccess = hasAccess;
      if (hasAccess && !this.calendarLoaded) {
        this.loadCalendars();
      }
    });

    this.calendarService.hasMicrosoftAccess().subscribe((hasAccess) => {
      this.hasMicrosoftAccess = hasAccess;
      if (hasAccess && !this.calendarLoaded) {
        this.loadCalendars();
      }
    });
  }

  public toggleCalendar(): void {
    if (window.innerWidth < 1024) {
      this.isCalendarExpanded = true;
      return;
    }
    this.isCalendarExpanded = !this.isCalendarExpanded;
  }

  public toggleMobileExpand(): void {
    if (window.innerWidth <= 1024) {
      this.isMobileExpanded = !this.isMobileExpanded;
    }
  }

  ngOnChanges() {
    if (this.hasGoogleAccess || this.hasMicrosoftAccess) {
      this.loadCalendars();
    }
  }

  public loadCalendars() {
    if (this.date) {
      const startDate = new Date(this.date);
      startDate.setHours(0, 0, 0, 0);
      const endDate = new Date(this.date);
      endDate.setHours(23, 59, 59, 999);

      this.calendarService.getCalendarEvents(startDate, endDate).subscribe({
        next: (events) => {
          if (!events || events.length === 0) {
            this.calendar = [];
            this.groupedCalendarEvents = {};
          } else {
            this.calendar = events;
            this.groupCalendarEvents(events);
          }
          this.calendarLoading = false;
          this.calendarLoaded = true;
        },
        error: (error) => {
          console.error('Error loading calendar events:', error);
          this.calendar = [];
          this.groupedCalendarEvents = {};
          this.calendarLoading = false;
          this.calendarLoaded = true;
        },
      });
    }
  }

  private groupCalendarEvents(events: CalendarEvent[]) {
    if (!events?.length) {
      this.groupedCalendarEvents = {};
      return;
    }

    this.groupedCalendarEvents = events.reduce(
      (groups: { [key: string]: CalendarEvent[] }, event) => {
        if (!event) {
          return groups;
        }

        const source = event.source || 'unknown';
        if (!groups[source]) {
          groups[source] = [];
        }
        groups[source].push(event);
        return groups;
      },
      {}
    );
  }

  public checkBoxChanged(event: Event, calendarItem: CalendarEvent) {
    const checkbox = event.target as HTMLInputElement;
    calendarItem.checked = checkbox.checked;

    if (this.calendar) {
      const selectedEvents = this.calendar.filter(event => event.checked);
      this.multipleEventsSelected.emit(selectedEvents);
    }
  }

  public loadFromCalendar(event: CalendarEvent) {
    if (!event) return;
    this.selectedCalenderId = event.id;
    this.calendarEventSelected.emit(event);
  }

  public calculateTimeDifferenceDates(start: string, end: string): string {
    const startDate = new Date(start);
    const endDate = new Date(end);
    const diffInMilliseconds = endDate.getTime() - startDate.getTime();
    const hours = Math.floor(diffInMilliseconds / (1000 * 60 * 60));
    const minutes = Math.floor(
      (diffInMilliseconds % (1000 * 60 * 60)) / (1000 * 60)
    );
    return `${hours}h ${minutes}m`;
  }

  public async requestCalendarAccess() {
    try {
      this.isConnectingGoogle = true;
      this.calendarService.initiateCalendarAccess();
      // Note: Loading state will be reset when the page redirects back from OAuth
    } catch (error) {
      console.error('Error requesting Google calendar access:', error);
      this.isConnectingGoogle = false;
    }
  }

  public async requestMicrosoftCalendarAccess(): Promise<void> {
    try {
      this.isConnectingMicrosoft = true;
      await this.calendarService.initiateMicrosoftCalendarAccess();
      // Note: Loading state will be reset when the page redirects back from OAuth
    } catch (error) {
      console.error('Error requesting Microsoft calendar access:', error);
      this.isConnectingMicrosoft = false;
    }
  }
} 